import { useEffect, useState, useSyncExternalStore } from "react";
import { useSession } from "@jkast90/kast-common/auth";
import {
  assignKey,
  bindings,
  canEditKeys,
  choiceOf,
  chooseProfile,
  DEFAULT_PROFILE,
  isCustomised,
  keyLabel,
  keymapVersion,
  LAYOUT_LABEL,
  layoutOf,
  movesOf,
  onKeymapChange,
  overridesFor,
  profileOf,
  resetKeys,
  setCapturing,
  turboKey,
  type KeyTarget,
  type Layout,
} from "../keymap";
import {
  controllerProfiles,
  createProfile,
  errorReason as reason,
  onControllersChange,
  onProfileSaveError,
} from "../controllers";
import { Button, Icon, Select, TextField, type SelectEntry } from "@jkast90/kast-common/ui";

// Buttons a console in a shared layout doesn't have: the Game Boy and
// Color share the Advance's keys but have no shoulder buttons.
const ABSENT: Record<string, string[]> = { gb: ["L", "R"], gbc: ["L", "R"] };

const FOLLOW = "follow";

/** Which key is being chosen: a button's slot, or the turbo key. */
interface Picking {
  button: string | null;
  slot: number;
}

/** Every shared profile, set off from what comes before. */
function sharedProfiles(): SelectEntry[] {
  const profiles = controllerProfiles().map((p) => ({ value: p.id, label: p.mine ? `${p.name} (yours)` : p.name }));
  return profiles.length ? [{ options: profiles }] : [];
}

/** Default, then every shared profile. */
function profileOptions(): SelectEntry[] {
  return [{ value: DEFAULT_PROFILE, label: "Default" }, ...sharedProfiles()];
}

/**
 * Which controller profile a game plays with: its own choice, or else the
 * one mapped to every game of its console, or else Default (the shared
 * layout with the player's changes). Signed in, the keys in use can be
 * saved as a new profile, which applies nowhere until picked.
 */
function ProfilePicker({
  game,
  console,
  setMessage,
}: {
  game: string;
  console: Layout;
  setMessage: (m: string | null) => void;
}) {
  useSyncExternalStore(onControllersChange, controllerProfiles);
  const signedIn = useSession().status === "signedIn";
  const [naming, setNaming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const target = { game, console };
  const profile = profileOf(target);
  const known = (id: string | null) =>
    id === DEFAULT_PROFILE || controllerProfiles().some((p) => p.id === id) ? id : null;
  const consoleChoice = known(choiceOf({ console })) ?? DEFAULT_PROFILE;
  const consoleName =
    consoleChoice === DEFAULT_PROFILE
      ? "Default"
      : (controllerProfiles().find((p) => p.id === consoleChoice)?.name ?? "Default");
  const gameOptions: SelectEntry[] = [
    { value: FOLLOW, label: `As all ${LAYOUT_LABEL[console]} games (${consoleName})` },
    { options: [{ value: DEFAULT_PROFILE, label: "Default" }] },
    ...sharedProfiles(),
  ];

  const save = async () => {
    const name = naming?.trim();
    if (!name) return;
    setBusy(true);
    setMessage(null);
    try {
      const made = await createProfile(name, overridesFor(target));
      setNaming(null);
      setMessage(`Saved as ${made.name}. Pick it above to play with it.`);
    } catch (error) {
      setMessage(reason(error, "Could not save the profile."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="controller-profile">
      <div className="field">
        <span className="field-label">This game</span>
        <div className="field-control">
          <Select
            variant="bare"
            className="dropdown"
            value={known(choiceOf({ game })) ?? FOLLOW}
            options={gameOptions}
            onChange={(id) => chooseProfile({ game }, id === FOLLOW ? null : id)}
            aria-label="Controller profile, this game"
          />
        </div>
      </div>
      <div className="field">
        <span className="field-label">All games</span>
        <div className="field-control">
          <Select
            variant="bare"
            className="dropdown"
            value={consoleChoice}
            options={profileOptions()}
            onChange={(id) => chooseProfile({ console }, id === DEFAULT_PROFILE ? null : id)}
            aria-label={`Controller profile, every ${LAYOUT_LABEL[console]} game`}
          />
          <span className="kui-muted kui-small">{LAYOUT_LABEL[console]}</span>
        </div>
      </div>
      {signedIn &&
        (naming === null ? (
          <div className="controller-profile-row">
            <Button size="sm" disabled={busy} onClick={() => setNaming("")}>
              <Icon name="add" size={20} className="icon" /> Save these keys as a profile
            </Button>
          </div>
        ) : (
          <form
            className="controller-profile-row"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <TextField
              variant="bare"
              autoFocus
              maxLength={40}
              placeholder="Profile name"
              aria-label="Profile name"
              value={naming}
              onChange={(e) => setNaming(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setNaming(null);
              }}
            />
            <Button variant="primary" size="sm" type="submit" disabled={busy || !naming.trim()}>
              Save
            </Button>
            <Button size="sm" disabled={busy} onClick={() => setNaming(null)}>
              Cancel
            </Button>
          </form>
        ))}
      <p className="kui-muted kui-small">
        {profile && !profile.editable
          ? `${profile.name} was shared by another player, so its keys stay as they are${signedIn ? "; save them as a profile to change a copy" : ""}.`
          : profile
            ? `${profile.name} is yours: changes reach everyone who plays with it.`
            : signedIn
              ? "Default: your own keys. Profiles are listed on the Controllers page."
              : "Default: your own keys, or pick a profile another player shared. Sign in to share yours."}
      </p>
    </div>
  );
}

/**
 * The keyboard for a console, from the shared layout and the player's
 * changes or a controller profile: one row per button. Click a key to press
 * a new one for it (Escape keeps the old), or add one; Reset returns to the
 * shared layout. With `game`, its profile is picked here too; with
 * `profile`, the keys are that profile's (null for Default).
 */
export default function KeyHelp({
  system: machine,
  game,
  profile,
}: {
  system: string;
  game?: string | null;
  profile?: string | null;
}) {
  const system = layoutOf(machine);
  useSyncExternalStore(onKeymapChange, keymapVersion);
  const [picking, setPicking] = useState<Picking | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const target: KeyTarget = profile !== undefined ? { profile } : game ? { game, console: system } : null;
  const targetKey = JSON.stringify(target);
  const editable = canEditKeys(target);

  useEffect(() => {
    onProfileSaveError((error) => setMessage(reason(error, "Could not save the profile's keys.")));
    return () => onProfileSaveError(() => {});
  }, []);

  // The next key pressed is the answer. Modifiers alone don't count.
  useEffect(() => {
    if (!picking) return;
    setCapturing(true);
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (/^(Shift|Control|Alt|Meta)/.test(e.code)) return;
      if (e.code !== "Escape")
        assignKey(system, picking.button, picking.slot, e.code, JSON.parse(targetKey) as KeyTarget);
      setPicking(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      setCapturing(false);
    };
  }, [picking, system, targetKey]);

  const rows = bindings(system, target).filter(({ button }) => !ABSENT[machine]?.includes(button));
  const key = (button: string | null, slot: number, code: string | undefined) => {
    const active = picking?.button === button && picking.slot === slot;
    if (!editable) return code ? <kbd key={slot}>{keyLabel(code)}</kbd> : null;
    return (
      <button
        key={slot}
        type="button"
        className={`key${active ? " picking" : ""}${code ? "" : " add"}`}
        title={code ? "Press a key to change this" : "Add a key"}
        onClick={() => setPicking(active ? null : { button, slot })}
      >
        {active ? "Press a key…" : code ? <kbd>{keyLabel(code)}</kbd> : <Icon name="add" size={20} className="icon" />}
      </button>
    );
  };

  return (
    <div className="key-help">
      {game && profile === undefined && <ProfilePicker game={game} console={system} setMessage={setMessage} />}
      {message && <p className="kui-small controller-message">{message}</p>}
      <ul className="kui-list-reset">
        {rows.map(({ button, keys }) => (
          <li key={button}>
            <span className="key-name kui-truncate">
              {button === "Up" || button === "Down" || button === "Left" || button === "Right"
                ? `${button} (${movesOf(system)})`
                : button}
            </span>
            <span className="key-caps">
              {keys.map((code, slot) => key(button, slot, code))}
              {keys.length < 3 && key(button, keys.length, undefined)}
            </span>
          </li>
        ))}
        <li>
          <span className="key-name kui-truncate">Fast-forward (hold)</span>
          <span className="key-caps">{key(null, 0, turboKey(target) || undefined)}</span>
        </li>
      </ul>
      {editable && isCustomised(system, target) && (
        <Button size="sm" onClick={() => resetKeys(system, target)}>
          Reset to the shared layout
        </Button>
      )}
    </div>
  );
}
